import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useWallet, useConnection } from '@solana/wallet-adapter-react'
import { PublicKey } from '@solana/web3.js'
import {
  Search,
  Layers,
  TrendingUp,
  DollarSign,
  AlertCircle,
  Loader2,
  ExternalLink,
  Vote,
  Clock,
  Target,
  ArrowRight,
  Info,
  Coins,
  Percent,
  HelpCircle
} from 'lucide-react'
import { cn } from '../lib/utils'
import { createCtoPoolsClient, lamportsToSol, Pool } from '../lib/anchorClient'
import { useJitoAPY } from '../hooks/useJitoAPY'

/**
 * Interface for pool data with additional UI metadata
 * Combines blockchain data with calculated display values
 * Updated for V2 with jitoSOL (totalPoolTokens) balance tracking
 */
interface PoolDisplayData {
  address: string
  tokenMint: string
  totalLiquidity: number
  jitoSolBalance: number
  protocolFee: number
  quorum: number
  activeProposal: boolean
  proposalCount: number
  participationRate?: number
}

/**
 * Pools Page Component
 * 
 * Search and view community funding pools with:
 * - Search input for token mint address
 * - Pool details display (liquidity, fee structure, active proposals)
 * - V2: Shows jitoSOL balance and estimated yield earnings
 * - V2: Displays ~7% APY liquid staking indicator
 * - Create proposal button
 * - Integration with blockchain to fetch pool data
 * - Loading and error states
 * - Responsive design with cyberpunk theme
 * - Full WCAG 2.1 AA accessibility
 */
export function Pools(): JSX.Element {
  const { formattedApy } = useJitoAPY()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const wallet = useWallet()
  const { connection } = useConnection()

  // State management
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '')
  const [searchedAddress, setSearchedAddress] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [searchError, setSearchError] = useState('')
  const [poolData, setPoolData] = useState<PoolDisplayData | null>(null)
  const [allPools, setAllPools] = useState<PoolDisplayData[]>([])
  const [isLoadingAll, setIsLoadingAll] = useState(true)

  /**
   * Validate if string is a valid Solana address
   * Uses PublicKey constructor to verify base58 encoding
   */
  const isValidSolanaAddress = (address: string): boolean => {
    try {
      new PublicKey(address)
      return true
    } catch {
      return false
    }
  }

  /**
   * Convert Pool account data to display format
   * Calculates percentage values and formats for UI
   * V2: Uses totalPoolTokens for jitoSOL balance (LST-based accounting)
   */
  const convertPoolToDisplay = (pool: Pool, address: string): PoolDisplayData => {
    // V2: totalPoolTokens is the jitoSOL balance, which is roughly 1:1 with SOL
    // (jitoSOL accrues ~7% APY, so its value gradually increases vs SOL)
    const jitoSolBalance = lamportsToSol(pool.totalPoolTokens)
    
    return {
      address,
      tokenMint: pool.tokenMint.toBase58(),
      totalLiquidity: jitoSolBalance, // For display purposes, use jitoSOL as liquidity
      jitoSolBalance: jitoSolBalance,
      protocolFee: pool.protocolFeeBps / 100, // Convert bps to percentage
      quorum: pool.quorumBps / 100, // Convert bps to percentage
      activeProposal: pool.activeProposal !== null,
      proposalCount: pool.proposalCount.toNumber()
    }
  }

  /**
   * Load all available pools from blockchain
   * Fetches all pool accounts and converts to display format
   */
  const loadAllPools = async () => {
    try {
      setIsLoadingAll(true)
      const client = createCtoPoolsClient(connection, wallet)
      const pools = await client.getAllPools()
      
      const displayPools = pools.map(p =>
        convertPoolToDisplay(p.account, p.publicKey.toBase58())
      )
      
      setAllPools(displayPools)
    } catch (error) {
      console.error('Error loading pools:', error)
      // Set empty array on error but don't show error message
      setAllPools([])
    } finally {
      setIsLoadingAll(false)
    }
  }

  /**
   * Search for a specific pool by token mint address
   * Validates address and fetches pool data from blockchain
   */
  const handleSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()

    const trimmedSearch = searchTerm.trim()
    
    // Validation
    if (!trimmedSearch) {
      setSearchError('Please enter a token mint address')
      return
    }

    if (!isValidSolanaAddress(trimmedSearch)) {
      setSearchError('Invalid Solana address format')
      return
    }

    setIsSearching(true)
    setSearchError('')
    setPoolData(null)
    setSearchedAddress(trimmedSearch)

    try {
      const client = createCtoPoolsClient(connection, wallet)
      const tokenMint = new PublicKey(trimmedSearch)
      const pool = await client.fetchPool(tokenMint)

      if (!pool) {
        setSearchError('No pool found for this token mint address')
        return
      }

      const [poolPDA] = client.getPoolPDA(tokenMint)
      setPoolData(convertPoolToDisplay(pool, poolPDA.toBase58()))
    } catch (error: any) {
      console.error('Error searching for pool:', error)
      setSearchError(error.message || 'Failed to search for pool. Please try again.')
    } finally {
      setIsSearching(false)
    }
  }

  /**
   * Navigate to pool details page
   * Opens the full pool page with all interactive features
   */
  const viewPoolDetails = (tokenMint: string) => {
    navigate(`/pool/${tokenMint}`)
  }

  /**
   * Load all pools on component mount
   * Auto-search if search param is provided in URL
   */
  useEffect(() => {
    loadAllPools()
    
    // Auto-search if URL param exists
    const searchParam = searchParams.get('search')
    if (searchParam) {
      setSearchTerm(searchParam)
      // Trigger search after a brief delay to allow state to update
      setTimeout(() => handleSearch(), 100)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-6xl mx-auto">
        
        {/* Page Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-cyber-purple/20 rounded-cyber mb-6">
            <Layers className="w-10 h-10 text-cyber-purple" aria-hidden="true" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gradient-cyber mb-4">
            Community Pools
          </h1>
          <p className="text-xl text-text-secondary max-w-2xl mx-auto mb-4">
            Search for existing pools or browse all active community funding pools
          </p>
          {/* V2 Liquid Staking Badge */}
          <div className="inline-flex items-center glass-card border border-cyber-green/50 px-4 py-2 rounded-full">
            <Coins className="w-5 h-5 text-cyber-green mr-2" aria-hidden="true" />
            <span className="text-cyber-green font-semibold">All pools earn {formattedApy} APY</span>
            <span className="text-text-secondary ml-2">via Jito Liquid Staking</span>
          </div>
        </div>

        {/* Search Section */}
        <div className="glass-card border border-dark-border p-8 mb-12">
          <form onSubmit={handleSearch} className="space-y-6">
            <div>
              <label htmlFor="pool-search" className="block text-lg font-medium text-text-primary mb-4">
                Search by Token Mint Address
              </label>
              <div className="relative">
                <input
                  id="pool-search"
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value)
                    setSearchError('')
                  }}
                  placeholder="Enter Solana token mint address..."
                  className={cn(
                    "w-full pl-12 pr-32 py-4 bg-dark-surface border rounded-cyber text-white placeholder-text-muted font-mono",
                    "focus:border-cyber-cyan focus:ring-1 focus:ring-cyber-cyan focus:outline-none transition-all duration-200",
                    searchError ? "border-status-error" : "border-dark-border"
                  )}
                  aria-invalid={!!searchError}
                  aria-describedby={searchError ? "search-error" : undefined}
                  disabled={isSearching}
                />
                <Search 
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-muted" 
                  aria-hidden="true" 
                />
                <button
                  type="submit"
                  disabled={!searchTerm.trim() || isSearching}
                  className={cn(
                    "absolute right-2 top-1/2 transform -translate-y-1/2",
                    "cyber-button-primary disabled:opacity-50 disabled:cursor-not-allowed px-6 py-2"
                  )}
                  aria-label="Search for pool"
                >
                  {isSearching ? (
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                  ) : (
                    'Search'
                  )}
                </button>
              </div>
              {searchError && (
                <p id="search-error" className="mt-2 text-sm text-status-error flex items-center space-x-2" role="alert">
                  <AlertCircle className="w-4 h-4" aria-hidden="true" />
                  <span>{searchError}</span>
                </p>
              )}
            </div>

            {/* Example */}
            <div className="glass-card bg-cyber-cyan/10 border border-cyber-cyan/20 p-4">
              <div className="flex items-start space-x-3">
                <Info className="w-5 h-5 text-cyber-cyan flex-shrink-0 mt-0.5" aria-hidden="true" />
                <div>
                  <p className="text-text-secondary">
                    <span className="text-cyber-cyan font-semibold">Example:</span> Wrapped SOL token mint is{' '}
                    <code className="bg-dark-panel px-2 py-1 rounded text-cyber-cyan font-mono text-sm">
                      So11111111111111111111111111111111111112
                    </code>
                  </p>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Search Results */}
        {poolData && searchedAddress && (
          <div className="mb-12 animate-slide-down">
            <h2 className="text-2xl font-bold text-text-primary mb-6 flex items-center">
              <Target className="w-6 h-6 text-cyber-cyan mr-3" aria-hidden="true" />
              Search Result
            </h2>
            
            <div className="glass-card border border-cyber-cyan/30 p-8">
              {/* Pool Header */}
              <div className="flex items-start justify-between mb-6 pb-6 border-b border-dark-border">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-cyber-cyan mb-2">Pool Details</h3>
                  <p className="text-sm text-text-muted font-mono break-all">
                    Token: {poolData.tokenMint}
                  </p>
                  <p className="text-sm text-text-muted font-mono break-all mt-1">
                    Pool: {poolData.address}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  {poolData.activeProposal && (
                    <div className="glass-card bg-cyber-green/10 border border-cyber-green/30 px-4 py-2">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-cyber-green rounded-full animate-pulse" aria-hidden="true" />
                        <span className="text-cyber-green font-semibold text-sm">Active Proposal</span>
                      </div>
                    </div>
                  )}
                  {/* Yield Badge */}
                  <div className="glass-card bg-cyber-green/10 border border-cyber-green/30 px-4 py-2">
                    <div className="flex items-center space-x-2">
                      <Percent className="w-4 h-4 text-cyber-green" aria-hidden="true" />
                      <span className="text-cyber-green font-semibold text-sm">Earning {formattedApy} APY</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pool Stats Grid - Updated for V2 */}
              <div className="grid md:grid-cols-4 gap-6 mb-8">
                <div className="text-center p-4 glass-dark rounded-cyber">
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    <DollarSign className="w-5 h-5 text-cyber-green" aria-hidden="true" />
                    <h4 className="text-cyber-green font-semibold">Total Value</h4>
                  </div>
                  <div className="text-3xl font-bold text-white">{poolData.totalLiquidity.toFixed(2)}</div>
                  <div className="text-sm text-text-muted">SOL equivalent</div>
                </div>

                <div className="text-center p-4 glass-dark rounded-cyber">
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    <Coins className="w-5 h-5 text-cyber-cyan" aria-hidden="true" />
                    <h4 className="text-cyber-cyan font-semibold">jitoSOL Balance</h4>
                    <button
                      className="text-text-muted hover:text-cyber-cyan transition-colors"
                      aria-label="What is jitoSOL?"
                      title={`jitoSOL is a liquid staking token backed 1:1 by staked SOL. It earns staking rewards (${formattedApy} APY) while remaining liquid.`}
                    >
                      <HelpCircle className="w-4 h-4" aria-hidden="true" />
                    </button>
                  </div>
                  <div className="text-3xl font-bold text-white">{poolData.jitoSolBalance.toFixed(2)}</div>
                  <div className="text-sm text-text-muted">earning {formattedApy} APY</div>
                </div>

                <div className="text-center p-4 glass-dark rounded-cyber">
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    <TrendingUp className="w-5 h-5 text-cyber-purple" aria-hidden="true" />
                    <h4 className="text-cyber-purple font-semibold">Protocol Fee</h4>
                  </div>
                  <div className="text-3xl font-bold text-white">{poolData.protocolFee}%</div>
                  <div className="text-sm text-text-muted">on execution</div>
                </div>

                <div className="text-center p-4 glass-dark rounded-cyber">
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    <Vote className="w-5 h-5 text-cyber-magenta" aria-hidden="true" />
                    <h4 className="text-cyber-magenta font-semibold">Quorum</h4>
                  </div>
                  <div className="text-3xl font-bold text-white">{poolData.quorum}%</div>
                  <div className="text-sm text-text-muted">required</div>
                </div>
              </div>

              {/* Additional Info */}
              <div className="glass-dark rounded-cyber p-4 mb-6">
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-text-secondary">Proposals Created:</span>
                    <span className="text-text-primary font-semibold">{poolData.proposalCount}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-text-secondary">Status:</span>
                    <span className={cn(
                      "font-semibold",
                      poolData.activeProposal ? "text-cyber-green" : "text-cyber-cyan"
                    )}>
                      {poolData.activeProposal ? 'Voting Active' : 'Open for Proposals'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Liquid Staking Info */}
              <div className="glass-card bg-cyber-green/10 border border-cyber-green/20 p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <Coins className="w-5 h-5 text-cyber-green flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <div>
                    <p className="text-text-secondary text-sm">
                      <span className="text-cyber-green font-semibold">Earning Yield:</span> Pool funds are staked via Jito Stake Pool,
                      earning {formattedApy} APY in staking rewards. jitoSOL automatically increases in value over time,
                      growing your community's treasury while waiting for governance decisions.
                    </p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => viewPoolDetails(poolData.tokenMint)}
                  className="cyber-button-primary flex items-center justify-center space-x-3 px-8 py-3 flex-1"
                >
                  <span>View Full Details</span>
                  <ArrowRight className="w-5 h-5" aria-hidden="true" />
                </button>
                <button
                  onClick={() => window.open(`https://solscan.io/token/${poolData.tokenMint}`, '_blank')}
                  className="cyber-button-secondary flex items-center justify-center space-x-3 px-8 py-3"
                >
                  <span>View on Solscan</span>
                  <ExternalLink className="w-4 h-4" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* All Pools Section */}
        <div>
          <h2 className="text-2xl font-bold text-text-primary mb-6 flex items-center">
            <Layers className="w-6 h-6 text-cyber-purple mr-3" aria-hidden="true" />
            All Active Pools
          </h2>

          {/* Loading State */}
          {isLoadingAll ? (
            <div className="glass-card border border-dark-border p-12 text-center">
              <Loader2 className="w-12 h-12 text-cyber-cyan animate-spin mx-auto mb-4" aria-hidden="true" />
              <p className="text-text-secondary">Loading pools from blockchain...</p>
            </div>
          ) : allPools.length === 0 ? (
            /* Empty State */
            <div className="glass-card border border-dark-border p-12 text-center">
              <Layers className="w-16 h-16 text-text-muted mx-auto mb-4" aria-hidden="true" />
              <h3 className="text-xl font-semibold text-text-primary mb-2">No Pools Found</h3>
              <p className="text-text-secondary mb-6">
                Be the first to create a community funding pool
              </p>
              <button
                onClick={() => navigate('/create-pool')}
                className="cyber-button-primary inline-flex items-center space-x-2 px-6 py-3"
              >
                <span>Create First Pool</span>
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </button>
            </div>
          ) : (
            /* Pools Grid */
            <div className="grid md:grid-cols-2 gap-6">
              {allPools.map((pool) => (
                <div
                  key={pool.address}
                  className="glass-card border border-dark-border hover:border-cyber-cyan/50 p-6 transition-all duration-300 cursor-pointer group"
                  onClick={() => viewPoolDetails(pool.tokenMint)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      viewPoolDetails(pool.tokenMint)
                    }
                  }}
                  aria-label={`View pool for token ${pool.tokenMint}`}
                >
                  {/* Pool Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-text-primary mb-1 group-hover:text-cyber-cyan transition-colors">
                        Pool #{pool.address.slice(0, 8)}
                      </h3>
                      <p className="text-xs text-text-muted font-mono truncate">
                        {pool.tokenMint}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {pool.activeProposal && (
                        <div className="glass-card bg-cyber-green/10 border border-cyber-green/30 px-3 py-1">
                          <div className="flex items-center space-x-1">
                            <Clock className="w-3 h-3 text-cyber-green" aria-hidden="true" />
                            <span className="text-cyber-green font-semibold text-xs">Voting</span>
                          </div>
                        </div>
                      )}
                      {/* Yield Badge */}
                      <div className="glass-card bg-cyber-green/10 border border-cyber-green/30 px-3 py-1">
                        <div className="flex items-center space-x-1">
                          <TrendingUp className="w-3 h-3 text-cyber-green" aria-hidden="true" />
                          <span className="text-cyber-green font-semibold text-xs">{formattedApy} APY</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pool Stats */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <div className="text-xs text-text-muted mb-1">Liquidity (jitoSOL)</div>
                      <div className="text-lg font-bold text-cyber-green">{pool.totalLiquidity.toFixed(2)} SOL</div>
                    </div>
                    <div>
                      <div className="text-xs text-text-muted mb-1">Proposals</div>
                      <div className="text-lg font-bold text-cyber-cyan">{pool.proposalCount}</div>
                    </div>
                  </div>

                  {/* Pool Info */}
                  <div className="glass-dark rounded-cyber p-3 text-xs space-y-2">
                    <div className="flex justify-between">
                      <span className="text-text-muted">Protocol Fee:</span>
                      <span className="text-text-primary font-semibold">{pool.protocolFee}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-muted">Quorum:</span>
                      <span className="text-text-primary font-semibold">{pool.quorum}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-muted">Staking:</span>
                      <span className="text-cyber-green font-semibold">Jito Liquid Staking</span>
                    </div>
                  </div>

                  {/* View Arrow */}
                  <div className="mt-4 pt-4 border-t border-dark-border flex items-center justify-between text-sm">
                    <span className="text-text-secondary group-hover:text-cyber-cyan transition-colors">
                      View Details
                    </span>
                    <ArrowRight className="w-4 h-4 text-cyber-cyan group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Create Pool CTA */}
        {!isLoadingAll && allPools.length > 0 && (
          <div className="mt-12 glass-card border border-cyber-cyan/30 p-8 text-center">
            <h3 className="text-2xl font-bold text-text-primary mb-4">
              Don't see your token?
            </h3>
            <p className="text-text-secondary mb-6">
              Create a new community funding pool for your favorite abandoned token.
              <span className="text-cyber-green font-semibold"> All pool funds earn {formattedApy} APY via Jito liquid staking!</span>
            </p>
            <button
              onClick={() => navigate('/create-pool')}
              className="cyber-button-primary inline-flex items-center space-x-3 px-8 py-3"
            >
              <span>Create New Pool</span>
              <ArrowRight className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default Pools
